
import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VoucherList, Voucher } from "@/components/dashboard/VoucherList";
import { Badge } from "@/components/ui/badge";
import { Copy, Gift, MapPin, QrCode, User } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

// Define types for the data we'll get from Supabase
type DatabaseVoucher = {
  id: string;
  code: string;
  status: 'active' | 'claimed' | 'redeemed' | 'expired';
  campaign_id: string;
  claimed_by: string | null;
  claimed_at: string | null;
  redeemed_at: string | null;
  redeemed_location: string | null;
  created_at: string;
  campaign: {
    title: string;
  };
  redeem_location?: {
    name: string;
  };
};

export default function VouchersList() {
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Fetch vouchers from Supabase with campaign and venue information
  const { data: databaseVouchers = [], isLoading, error } = useQuery({
    queryKey: ['vouchers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vouchers')
        .select(`
          *,
          campaign:campaign_id(title),
          redeem_location:redeemed_location(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as DatabaseVoucher[];
    }
  });
  
  // Transform database vouchers to the format expected by the VoucherList component
  const vouchers: Voucher[] = databaseVouchers.map(dbVoucher => ({
    id: dbVoucher.id,
    code: dbVoucher.code,
    campaign: dbVoucher.campaign?.title || 'Unknown Campaign',
    status: dbVoucher.status,
    expires: new Date(dbVoucher.campaign_id ? new Date().setDate(new Date().getDate() + 30) : new Date()), // Placeholder
    claimed: dbVoucher.claimed_at ? {
      by: dbVoucher.claimed_by || 'Unknown User',
      date: new Date(dbVoucher.claimed_at)
    } : undefined,
    redeemed: dbVoucher.redeemed_at ? {
      date: new Date(dbVoucher.redeemed_at),
      location: dbVoucher.redeem_location?.name || 'Unknown Location'
    } : undefined
  }));
  
  const handleViewVoucher = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setIsDialogOpen(true);
  };
  
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success("Voucher code copied to clipboard");
  };
  
  const statusColors = {
    active: "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400",
    redeemed: "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400",
    expired: "bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400",
    claimed: "bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-400"
  };
  
  return (
    <AdminLayout 
      title="Vouchers" 
      subtitle="Manage voucher codes for your campaigns"
    >
      {error ? (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          <p>Failed to load vouchers. Please try again later.</p>
          <p className="text-sm mt-2">{String(error)}</p>
        </div>
      ) : (
        <VoucherList 
          vouchers={vouchers} 
          onViewVoucher={handleViewVoucher}
          isLoading={isLoading}
        />
      )}
      
      {/* Voucher Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          {selectedVoucher && (
            <>
              <DialogHeader>
                <DialogTitle>Voucher Details</DialogTitle>
                <DialogDescription>
                  Information about this voucher code.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Voucher Code</p>
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 rounded bg-secondary font-mono text-sm">
                        {selectedVoucher.code}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleCopyCode(selectedVoucher.code)}
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  </div>
                  <Badge className={statusColors[selectedVoucher.status]}>
                    {selectedVoucher.status}
                  </Badge>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm font-medium">Campaign</p>
                  <p>{selectedVoucher.campaign}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Expires On</p>
                    <p>{selectedVoucher.expires.toLocaleDateString()}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Status</p>
                    <p className="capitalize">{selectedVoucher.status}</p>
                  </div>
                </div>
                
                <Separator />
                
                {selectedVoucher.claimed && (
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <User size={16} className="mr-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Claimed Information</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Claimed By</p>
                        <p className="text-sm">{selectedVoucher.claimed.by}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Claimed Date</p>
                        <p className="text-sm">{selectedVoucher.claimed.date.toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedVoucher.redeemed && (
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Gift size={16} className="mr-2 text-muted-foreground" />
                      <p className="text-sm font-medium">Redemption Information</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Redeemed Date</p>
                        <p className="text-sm">{selectedVoucher.redeemed.date.toLocaleDateString()}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Redeemed Location</p>
                        <div className="flex items-center">
                          <MapPin size={12} className="mr-1 text-muted-foreground" />
                          <p className="text-sm">{selectedVoucher.redeemed.location}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-6 flex justify-center">
                  <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <QrCode size={120} className="text-primary" />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
